import React from 'react';
import { MapPin, Clock, Phone, Mail, CheckCircle2, Wine } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import SEOHelmet from '../../components/SEOHelmet';
import RelatedCities from '../../components/RelatedCities';
import { companyInfo } from '../../mockData';

const TemeculaPage = () => {
  return (
    <div style={{ backgroundColor: 'var(--bg-page)', minHeight: '100vh' }}>
      <SEOHelmet 
        title="Process Server Temecula CA 92591 | Wine Country Legal Services | Attorney Support"
        description="Expert process server in Temecula, CA 92591. Wine industry specialists serving attorneys & businesses in Temecula Valley. Court filing, skip tracing, document retrieval. Licensed & bonded. Request a quote online."
        keywords="process server Temecula CA 92591, Temecula wine country process serving, Southwest Justice Center, legal document service Temecula Valley, attorney service wine country, court filing 92591"
        canonical="/cities/temecula"
      />
      <Header />

      {/* Hero Section */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '160px 0 80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto' }}>
            <h1 className="brand-display" style={{
              marginBottom: '1.5rem',
              color: 'var(--text-primary)'
            }}>
              Process Server Temecula, California
            </h1>
            <p className="body-large" style={{
              color: 'var(--text-secondary)',
              marginBottom: '2rem',
              lineHeight: '1.6'
            }}>
              Professional legal document services in Temecula, CA 92590-92593. Expert, registered and bonded process servers serving the wine country, Old Town Temecula historic district, Redhawk and Wolf Creek residential communities, and the Civic Center business area. We specialize in wine industry legal matters, tourism businesses, and upscale residential communities throughout the Temecula Valley.
            </p>
            
            <div style={{
              backgroundColor: 'var(--bg-card)',
              padding: '1.5rem',
              borderRadius: '8px',
              border: '1px solid var(--border-light)',
              marginBottom: '2rem',
              textAlign: 'left'
            }}>
              <h3 style={{ color: 'var(--brand-primary)', marginBottom: '1rem', fontSize: '1.1rem' }}>
                🏛️ Temecula Local Court & Government Information
              </h3>
              <p style={{ color: 'var(--text-primary)', marginBottom: '0.5rem' }}>
                <strong>Southwest Justice Center</strong><br />
                30755-B Auld Rd, Murrieta, CA 92563 (10 minutes from Temecula)
              </p>
              <p style={{ color: 'var(--text-primary)', marginBottom: '0.5rem' }}>
                <strong>Temecula Civic Center</strong><br />
                41000 Main St, Temecula, CA 92590 (Business licensing, permits)
              </p>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                Primary courts for Temecula Valley legal matters. We handle wine industry business filings, tourism sector litigation, and residential community legal services.
              </p>
            </div>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/place-order" className="btn-primary" style={{ textDecoration: 'none' }}>
                Place Order Now
              </Link>
              <Link to="/quote" className="btn-secondary" style={{ textDecoration: 'none' }}>
                Get Free Quote
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services & Wine Country Focus */}
      <section style={{ backgroundColor: 'var(--bg-card)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Temecula Legal Document Services
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
              Specialized legal support services for Temecula's wine country, business district, and affluent valley community.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minWidth(300px, 1fr))', gap: '2rem' }}>
            {/* Services */}
            <div style={{
              backgroundColor: 'var(--bg-page)',
              padding: '2rem',
              borderRadius: '12px',
              border: '1px solid var(--border-medium)'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center' }}>
                <CheckCircle2 size={24} color="var(--brand-primary)" style={{ marginRight: '0.5rem' }} />
                Wine Country & Business Services
              </h3>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                {[
                  'Process Serving & Legal Notices',
                  'Business Document Filing',
                  'County Recording Services',
                  'Corporate Document Retrieval',
                  'Skip Tracing & Locate Services'
                ].map((service, index) => (
                  <li key={service} style={{ 
                    padding: '0.75rem 0',
                    borderBottom: index < 4 ? '1px solid var(--border-light)' : 'none',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <CheckCircle2 size={16} color="var(--brand-primary)" style={{ marginRight: '0.75rem' }} />
                    <span style={{ color: 'var(--text-primary)' }}>{service}</span>
                  </li>
                ))}
              </ul>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginTop: '1rem' }}>
                Specialized experience with wine industry, hospitality sector, and business district legal document needs.
              </p>
            </div>

            {/* Wine Country Expertise */}
            <div style={{
              backgroundColor: 'var(--bg-page)',
              padding: '2rem',
              borderRadius: '12px',
              border: '1px solid var(--border-medium)'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center' }}>
                <Wine size={24} color="var(--brand-primary)" style={{ marginRight: '0.5rem' }} />
                Wine Country & Tourism Expertise
              </h3>
              <div style={{ color: 'var(--text-primary)', lineHeight: '1.6' }}>
                <p style={{ marginBottom: '1rem' }}>
                  <strong>Wine Industry Experience</strong><br />
                  <span style={{ color: 'var(--text-secondary)' }}>Specialized service for wineries and vineyard businesses</span>
                </p>
                <p style={{ marginBottom: '1rem' }}>
                  <strong>Tourism & Hospitality Sector</strong><br />
                  <span style={{ color: 'var(--text-secondary)' }}>Expert service for hotels, resorts, and tourism businesses</span>
                </p>
                <p style={{ marginBottom: '1rem' }}>
                  <strong>Affluent Community Service</strong><br />
                  <span style={{ color: 'var(--text-secondary)' }}>Professional service for upscale residential and business clients</span>
                </p>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', fontStyle: 'italic' }}>
                  Deep understanding of Temecula's unique blend of wine industry, tourism, and affluent valley lifestyle.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Business & Tourism Focus */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Why Choose Us for Temecula?
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '700px', margin: '0 auto' }}>
              Wine country expertise meets professional standards. We understand Temecula's unique business environment and affluent community needs.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minWidth(250px, 1fr))', gap: '2rem' }}>
            {[
              {
                icon: <Wine size={32} />,
                title: 'Wine Industry Knowledge',
                description: 'Specialized experience with winery businesses, vineyard operations, and wine country legal matters.'
              },
              {
                icon: <Clock size={32} />,
                title: 'Tourism Season Ready',
                description: 'Flexible service levels to accommodate seasonal tourism and hospitality industry fluctuations.'
              },
              {
                icon: <CheckCircle2 size={32} />,
                title: 'Affluent Community Standards',
                description: 'Registered, bonded, and experienced with high-end residential and business community expectations.'
              }
            ].map((feature) => (
              <div key={feature.title} style={{
                backgroundColor: 'var(--bg-card)',
                padding: '2rem',
                borderRadius: '12px',
                border: '1px solid var(--border-medium)',
                textAlign: 'center'
              }}>
                <div style={{ color: 'var(--brand-primary)', marginBottom: '1rem', display: 'flex', justifyContent: 'center' }}>
                  {feature.icon}
                </div>
                <h3 className="heading-3" style={{ marginBottom: '1rem' }}>{feature.title}</h3>
                <p className="body-text" style={{ color: 'var(--text-secondary)' }}>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Area Coverage */}
      <section style={{ backgroundColor: 'var(--bg-card)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Temecula Service Coverage
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
              Serving Temecula and the entire Temecula Valley from our wine country business district location.
            </p>
          </div>

          <div style={{
            backgroundColor: 'var(--bg-page)',
            padding: '2rem',
            borderRadius: '12px',
            border: '1px solid var(--border-medium)',
            textAlign: 'center'
          }}>
            <h3 className="heading-3" style={{ marginBottom: '1.5rem' }}>
              Temecula Valley Communities Served
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minWidth(180px, 1fr))',
              gap: '1rem',
              marginBottom: '2rem'
            }}>
              {[
                'Temecula', 'Murrieta', 'Menifee', 'Lake Elsinore',
                'Wildomar', 'Canyon Lake', 'Winchester', 'French Valley',
                'Fallbrook', 'Rainbow', 'De Luz', 'Pala'
              ].map((city, index) => (
              <div key={city} style={{
                  padding: '0.75rem',
                  backgroundColor: index === 0 ? 'var(--brand-primary)' : 'var(--bg-card)',
                  borderRadius: '6px',
                  border: '1px solid var(--border-light)'
                }}>
                  <span style={{ 
                    color: index === 0 ? 'white' : 'var(--text-primary)',
                    fontWeight: index === 0 ? '600' : 'normal'
                  }}>{city}</span>
                </div>
              ))}
            </div>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
              Complete coverage of Temecula Valley wine country and southwestern Riverside County communities. We also serve the <Link to="/cities/murrieta" style={{ color: 'var(--brand-primary)', textDecoration: 'none' }}>master-planned community of Murrieta</Link>, growing <Link to="/cities/menifee" style={{ color: 'var(--brand-primary)', textDecoration: 'none' }}>Menifee</Link>, and throughout the region.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" style={{ backgroundColor: 'var(--bg-page)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Contact Us for Temecula Services
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
              Ready to get started? Contact Legal Document Specialist for professional wine country and business district service in Temecula.
            </p>
          </div>

          <div style={{
            maxWidth: '600px',
            margin: '0 auto',
            backgroundColor: 'var(--bg-card)',
            padding: '2rem',
            borderRadius: '12px',
            border: '1px solid var(--border-medium)',
            textAlign: 'center'
          }}>
            <div style={{ marginBottom: '2rem' }}>
              <Phone size={24} color="var(--brand-primary)" style={{ marginBottom: '0.5rem' }} />
              <div style={{ fontWeight: '600', color: 'var(--text-primary)', fontSize: '1.2rem' }}>{companyInfo.phone}</div>
              <div style={{ color: 'var(--text-secondary)' }}>Monday-Friday 9AM-5PM PST</div>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <Mail size={24} color="var(--brand-primary)" style={{ marginBottom: '0.5rem' }} />
              <div style={{ fontWeight: '600', color: 'var(--text-primary)' }}>{companyInfo.email}</div>
              <div style={{ color: 'var(--text-secondary)' }}>Wine industry and business district services</div>
            </div>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '2rem' }}>
              <Link to="/place-order" className="btn-primary" style={{ textDecoration: 'none' }}>
                Place Order Online
              </Link>
              <Link to="/quote" className="btn-secondary" style={{ textDecoration: 'none' }}>
                Request Quote
              </Link>
            </div>

            <div style={{ 
              backgroundColor: 'var(--bg-page)', 
              padding: '1rem', 
              borderRadius: '6px',
              border: '1px solid var(--border-light)'
            }}>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', margin: 0 }}>
                <strong>Serving Wine Country & Business District</strong><br />
                Wine Industry Expertise • Tourism Specialists • Affluent Community Standards
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Related Cities Section */}
      <RelatedCities currentCity="temecula" region="central-riverside" />

      <Footer />
    </div>
  );
};

export default TemeculaPage;